import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Activity, DollarSign, Download, Users as UsersIcon, Webhook } from "lucide-react";
import { atlasFetch, keyMode } from "@/lib/atlas-api";
import { downloadCsv } from "@/lib/atlas-csv";
import { Card, Stat, Skeleton, ErrorState } from "@/components/portal/PortalPrimitives";

function pct(n) {
  if (n == null || isNaN(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}
function usd(n) {
  if (n == null) return "$0.00";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

export default function PortalDashboard() {
  const { me } = useOutletContext();
  const [usage, setUsage] = useState(null);
  const [deliveries, setDeliveries] = useState(null);
  const [err, setErr] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const env = keyMode();
  const envTag = env === "production" ? "production" : "sandbox";

  useEffect(() => {
    atlasFetch("/api/v1/usage").then(setUsage).catch((e) => setErr(e.message));
    atlasFetch("/api/v1/webhooks/deliveries?limit=100", { needsIngestSecret: true })
      .then((d) => setDeliveries(Array.isArray(d) ? d : d?.items || []))
      .catch(() => setDeliveries([]));
  }, []);

  const dl = async (label, opts) => {
    setDownloading(label);
    try {
      await downloadCsv(opts);
      toast.success(`${label} downloaded`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDownloading(null);
    }
  };

  const loadingUsage = !usage && !err;
  const total = usage ? (usage.production?.calls || 0) + (usage.sandbox?.calls || 0) : 0;
  const uniqueTotal = usage
    ? (usage.production?.unique_end_users || 0) + (usage.sandbox?.unique_end_users || 0)
    : 0;
  const successRate = deliveries && deliveries.length
    ? deliveries.filter((d) => d.status === "delivered").length / deliveries.length
    : null;

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Dashboard</p>
          <h1 className="font-display text-[28px] text-[#0A1128] mt-1.5">
            Welcome back{me?.company ? `, ${me.company}` : me?.name ? `, ${me.name}` : ""}
          </h1>
          {usage && (
            <p className="mt-1 text-[13px] text-slate-500">
              Billing month: <span className="font-mono">{usage.month}</span> · rate ${usage.rate_per_user_usd}/user
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="dashboard-download-usage"
            disabled={downloading === "Usage"}
            onClick={() =>
              dl("Usage", {
                path: "/api/v1/reports/usage.csv",
                filename: `atlas-usage-${new Date().toISOString().slice(0,10)}.csv`,
                needsIngest: false,
                params: { months: 6 },
              })
            }
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white text-[#0A1128] text-[12.5px] font-medium px-4 py-2 hover:border-[#1A4232]/50 transition-colors disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            {downloading === "Usage" ? "Preparing…" : "Download usage CSV"}
          </button>
          <button
            data-testid="dashboard-download-accounts"
            disabled={downloading === "Linked accounts"}
            onClick={() =>
              dl("Linked accounts", {
                path: "/api/v1/reports/calculations.csv",
                filename: `atlas-${envTag}-linked-accounts-${new Date().toISOString().slice(0,10)}.csv`,
                needsIngest: true,
              })
            }
            className="inline-flex items-center gap-2 rounded-full bg-[#1A4232] text-[#F5F1EB] text-[12.5px] font-medium px-4 py-2 hover:bg-[#0E2A20] transition-colors disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            {downloading === "Linked accounts" ? "Preparing…" : "Download linked accounts CSV"}
          </button>
        </div>
      </div>

      {err && <ErrorState testId="dashboard-error" title="Couldn't load usage" message={err} />}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          testId="stat-total-calls"
          label="API calls"
          value={total.toLocaleString()}
          sub={usage ? `prod ${(usage.production?.calls || 0).toLocaleString()} · sbx ${(usage.sandbox?.calls || 0).toLocaleString()}` : "—"}
          loading={loadingUsage}
        />
        <Stat
          testId="stat-unique-users"
          label="Unique end-users"
          value={uniqueTotal.toLocaleString()}
          sub={usage ? `prod ${usage.production?.unique_end_users || 0} · sbx ${usage.sandbox?.unique_end_users || 0}` : "—"}
          loading={loadingUsage}
        />
        <Stat
          testId="stat-bill"
          label="Estimated bill"
          value={usd(usage?.production?.estimated_bill_usd)}
          sub={usage ? `prod only · ${usage.month}` : "—"}
          loading={loadingUsage}
          accent
        />
        <Stat
          testId="stat-webhook-success"
          label="Webhook success rate"
          value={successRate == null ? "—" : pct(successRate)}
          sub={deliveries == null ? "" : deliveries.length === 0 ? "No deliveries yet" : `${deliveries.length} recent`}
          loading={deliveries === null}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card testId="card-production">
          <div className="flex items-center justify-between">
            <p className="text-[12px] uppercase tracking-[0.16em] text-emerald-700 font-semibold">Production</p>
            <Activity className="h-4 w-4 text-emerald-700" />
          </div>
          {!usage ? (
            <>
              <Skeleton className="mt-4 h-7 w-24" />
              <Skeleton className="mt-2 h-3 w-40" />
            </>
          ) : (
            <>
              <p className="mt-3 font-display text-[24px] text-[#0A1128]">{(usage.production?.calls || 0).toLocaleString()} calls</p>
              <p className="text-[13px] text-slate-600 mt-1">
                {(usage.production?.unique_end_users || 0).toLocaleString()} unique users · billed {usd(usage.production?.estimated_bill_usd)}
              </p>
            </>
          )}
        </Card>
        <Card testId="card-sandbox">
          <div className="flex items-center justify-between">
            <p className="text-[12px] uppercase tracking-[0.16em] text-amber-700 font-semibold">Sandbox</p>
            <DollarSign className="h-4 w-4 text-amber-700" />
          </div>
          {!usage ? (
            <>
              <Skeleton className="mt-4 h-7 w-24" />
              <Skeleton className="mt-2 h-3 w-40" />
            </>
          ) : (
            <>
              <p className="mt-3 font-display text-[24px] text-[#0A1128]">{(usage.sandbox?.calls || 0).toLocaleString()} calls</p>
              <p className="text-[13px] text-slate-600 mt-1">
                {(usage.sandbox?.unique_end_users || 0).toLocaleString()} unique users · not billed
              </p>
            </>
          )}
        </Card>
      </div>

      <Card testId="card-quick-links">
        <p className="font-display text-[16px] text-[#0A1128] mb-3">Quick actions</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <a href="/portal/users" className="rounded-[10px] border border-slate-200 px-4 py-3 hover:border-[#1A4232]/40 transition-colors">
            <p className="font-medium text-[13.5px] text-[#0A1128] flex items-center gap-2"><UsersIcon className="h-4 w-4" /> End-users</p>
            <p className="text-[12px] text-slate-500 mt-1">Browse & GDPR-delete</p>
          </a>
          <a href="/portal/webhooks" className="rounded-[10px] border border-slate-200 px-4 py-3 hover:border-[#1A4232]/40 transition-colors">
            <p className="font-medium text-[13.5px] text-[#0A1128] flex items-center gap-2"><Webhook className="h-4 w-4" /> Webhooks</p>
            <p className="text-[12px] text-slate-500 mt-1">Config + delivery log</p>
          </a>
          <a href="/portal/keys" className="rounded-[10px] border border-slate-200 px-4 py-3 hover:border-[#1A4232]/40 transition-colors">
            <p className="font-medium text-[13.5px] text-[#0A1128]">API Keys</p>
            <p className="text-[12px] text-slate-500 mt-1">View, rotate, reveal ingest secret</p>
          </a>
        </div>
      </Card>
    </div>
  );
}
